import { Injectable } from '@angular/core';
import { UserContextService } from '../core/services/user-context.service';
import { SharedService } from '../core/services/shared-service';
import { IMetadataProvider, IRunViewProvider, LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider, GraphQLProviderConfigData } from '@memberjunction/graphql-dataprovider';
import { BehaviorSubject, Observable } from 'rxjs';
import { InstanceComponentEntity, InstanceEntity } from 'mj_generatedentities';
import { ResourceTypeEntity } from '@memberjunction/core-entities';
import { MJEventType, MJGlobal } from '@memberjunction/global';

const MJAPI_COMPONENT_NAME = 'MJAPI';

@Injectable({
  providedIn: 'root'
})
export class InstanceProviderService {
  private md: Metadata;
  private _instanceProvider: IMetadataProvider;
  private _instanceComponent: InstanceComponentEntity;

  private _isInstanceLoaded = new BehaviorSubject<'loading' | 'loaded' | 'failed'>('loading');
  // Expose as an Observable to maintain immutability
  public get isInstanceLoaded$(): Observable<'loading' | 'loaded' | 'failed'> {
    return this._isInstanceLoaded.asObservable();
  }

  constructor(private userContextService: UserContextService, private sharedService: SharedService) {
    this.md = new Metadata();
  }
 
  public get InstanceProvider(): IMetadataProvider {
    return this._instanceProvider;
  }

  public get InstanceRunViewProvider(): IRunViewProvider {
    return <IRunViewProvider><any>this._instanceProvider;
  }

  public get CurrentInstance(): InstanceEntity {
    return this.userContextService.CurrentInstance;
  }

  private async getMJAPIInstanceComponent(instance: InstanceEntity, forceRefresh: boolean = false): Promise<InstanceComponentEntity> {
    if (this._instanceComponent && !forceRefresh) {
      return this._instanceComponent;
    }

    const rv = new RunView();
    const instanceComponentResult = await rv.RunView<InstanceComponentEntity>({
        EntityName: 'Instance Components',
        ExtraFilter: `Component='${MJAPI_COMPONENT_NAME}' AND InstanceID='${instance.ID}'`,
        ResultType: 'entity_object'
      }, 
      this.md.CurrentUser
    );

    if (instanceComponentResult.Success && instanceComponentResult.Results.length == 1) {
      this._instanceComponent = instanceComponentResult.Results[0];
      return instanceComponentResult.Results[0];
    } 
    else {
      throw new Error('Error getting MJAPI instance component');
    }
  }

  public async SetupInstanceProvider(forceRefresh: boolean = false) {
    if (!forceRefresh && this._instanceProvider) {
      return;
    }
    
    try {
      this.setInstanceProviderLoaded('loading');
      const g = new GraphQLDataProvider();
      const token = GraphQLDataProvider.Instance.ConfigData.Token;

      const instance = this.userContextService.CurrentInstance;
      if (instance) {
        const apiComponent = await this.getMJAPIInstanceComponent(instance, forceRefresh);
        
        if (apiComponent) {
          const url = apiComponent.URL;
          const wsurl = this.getWSUrl(url);

          const config = new GraphQLProviderConfigData(token, url, wsurl, null);
          await g.Config(config, true);    
          this._instanceProvider = g;

          const rv = new RunView(this.InstanceRunViewProvider);
          const result = await rv.RunView<ResourceTypeEntity>({
            EntityName: 'Resource Types',
          }, this.InstanceProvider.CurrentUser)
          this._resourceTypes = result.Results;
    
          this.setInstanceProviderLoaded('loaded');
        } 
        else {
          throw new Error(`MJAPI component not found for instance ${instance.Name} with ID: ${instance.ID}`);
        }
      } 
      else {
        throw new Error(`Instance not loaded for Organization ${this.userContextService.Organization?.Name} with ID: ${this.userContextService.Organization?.ID}`);
      }
    } 
    catch (e) {
      const errorMessage = `Error setting up instance provider for skip: ${e}`;
      LogError(errorMessage);
      this.setInstanceProviderLoaded('failed');
      this.sharedService.DisplayNotification(errorMessage, 'error');
    }
  }

  public async TriggerInstanceProviderSetup(instanceId: string) {
    await this.SetupInstanceProvider(true);
    MJGlobal.Instance.RaiseEvent({
      event: MJEventType.ComponentEvent,
      eventCode: 'InstanceChanged',
      component: this,
      args: {
        instanceId: instanceId
      }
    });
  }

  private getWSUrl(url: string): string {
    return url.replace(/^(https?:\/\/)?/, 'wss://');
  }

  private setInstanceProviderLoaded(value: 'loading' | 'loaded' | 'failed') {
    this._isInstanceLoaded.next(value);
  }
 
  private _resourceTypes: ResourceTypeEntity[] = [];
  public get ResourceTypes(): ResourceTypeEntity[] {
    return this._resourceTypes;
  }

  public get ReportResourceType(): ResourceTypeEntity {
    return this._resourceTypes.find(rt => rt.Name === 'Reports')!;
  }
}
